import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import castArray from 'lodash/castArray';
import uniqBy from 'lodash/uniqBy';
import { Labeled } from 'react-admin';
import { useWatch, useFormContext } from 'react-hook-form';

import Item from './attachment-item';
import Selector from './attachment-selector';
import Uploader from './attachment-uploader';

const AttachmentsInput = ({
    accept,
    fullWidth = true,
    helperText,
    itemLabel,
    label = 'Attachments',
    hideThumbnail,
    source,
    validate,
    articleSuitable,
    ...props
}) => {
    const { control, setValue, trigger, formState: { errors } } = useFormContext();
    const watchedValue = useWatch({ name: source, control });

    // Create input-like object for compatibility
    const input = React.useMemo(() => {
        const value = watchedValue || [];
        return {
            value,
            onChange(newValue) {
                setValue(source, newValue);
                trigger(source);
            },
            onBlur: () => trigger(source),
        };
    }, [watchedValue, setValue, trigger, source]);

    // Create meta-like object for compatibility
    const meta = {
        touched: true,
        error: errors[source]?.message,
    };

    const isRequired = validate && (
        Array.isArray(validate) ?
            validate.some((v) => v.name === 'required' || v.toString().includes('required')) :
            validate.name === 'required' || validate.toString().includes('required')
    );

    const handleAttachmentsAdded = useCallback((attachments) => {
        attachments = castArray(attachments);
        const newAttachments = uniqBy([
            ...(input.value || []),
            ...attachments,
        ], 'id');

        input.onChange(newAttachments);
        input.onBlur();
    }, [input]);

    const handleAttachmentRemoved = useCallback((attachment) => {
        const filteredAttachments = (input.value || []).filter((a) => a.id !== attachment.id);

        input.onChange(filteredAttachments);
        input.onBlur();
    }, [input]);

    const handleAttachmentUpdated = useCallback((attachment) => {
        const filteredAttachments = (input.value || []).map((a) => a.id === attachment.id ? attachment : a);

        input.onChange(filteredAttachments);
        input.onBlur();
    }, [input]);

    return (
        <Labeled
            fullWidth={fullWidth}
            label={label}
            isRequired={isRequired}
            meta={meta}
            input={input}
            resource={props.resource}
            source={props.source}
        >
            <Grid container sx={{ width: '100%' }} spacing={2} >
                <Grid size={12}>
                    <Uploader accept={accept} onAttachmentsAdded={handleAttachmentsAdded} />
                </Grid>
                <Grid size={12}>
                    <Selector
                        articleSuitable={articleSuitable}
                        onAttachmentSelected={handleAttachmentsAdded}
                    />
                </Grid>
                {
                    (input.value || []).map((attachment, index) => {
                        const source = `${props.source}.${index}`;

                        return (
                            <Grid key={source} size={12}>
                                <Item
                                    attachment={attachment}
                                    label={itemLabel}
                                    hideThumbnail={hideThumbnail}
                                    onAttachmentRemoved={handleAttachmentRemoved}
                                    onAttachmentUpdated={handleAttachmentUpdated}
                                />
                            </Grid>
                        );
                    })
                }
                { meta.touched && meta.error ? <Grid size={12}><FormHelperText>{meta.error}</FormHelperText></Grid> : null }
                { helperText ? <Grid size={12}><FormHelperText>{helperText}</FormHelperText></Grid> : null }
            </Grid>
        </Labeled>
    );
};

AttachmentsInput.propTypes = {
    accept: PropTypes.object,
    fullWidth: PropTypes.bool,
    helperText: PropTypes.string,
    itemLabel: PropTypes.string,
    label: PropTypes.string,
    hideThumbnail: PropTypes.bool,
    resource: PropTypes.string,
    source: PropTypes.string.isRequired,
    validate: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.arrayOf(PropTypes.func),
    ]),
};

export default React.memo(AttachmentsInput);
